import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useUser } from '@clerk/react';
import { isTeamAdminOrOwner } from '@this+that/models';

import { APP_PAGES } from '../../routes/constants';
import { useActiveTeamId, useTeamStore } from '../../store/Team/useTeamStore';
import {
  getTrialBannerStage,
  TrialBannerStage,
  TrialBannerState,
} from '../../utils/billing/getTrialBannerStage';
import {
  buildTrialBannerDismissKey,
  isTrialBannerDismissible,
  readTrialBannerDismissed,
  writeTrialBannerDismissed,
} from '../../utils/billing/trialBannerDismissal';

interface ShouldShowTrialBannerParams {
  trialBannerState: TrialBannerState | null;
  pathname: string;
  isDismissed: boolean;
}

// Visible to every team role, not just owners/admins — a plain member sees the
// same bar with a "ask your owner" subtitle instead of the CTA (see `canManage`
// below), rather than never learning the trial is ending.
export function shouldShowTrialBanner({
  trialBannerState,
  pathname,
  isDismissed,
}: ShouldShowTrialBannerParams): boolean {
  if (!trialBannerState) {
    return false;
  }
  // /billing already shows the same state, in more detail.
  if (pathname.startsWith(APP_PAGES.BILLING)) {
    return false;
  }
  if (isTrialBannerDismissible(trialBannerState.stage) && isDismissed) {
    return false;
  }
  return true;
}

export interface UseTrialBannerResult {
  isVisible: boolean;
  stage: TrialBannerStage | null;
  daysLeft: number;
  trialEndsAt: number | undefined;
  teamId: string | undefined;
  isDismissible: boolean;
  canManage: boolean;
  dismiss: () => void;
  goToBilling: () => void;
}

export function useTrialBanner(): UseTrialBannerResult {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useUser();
  const userId = user?.id;

  const teamId = useActiveTeamId();
  const userRole = useTeamStore((s) =>
    teamId ? s.teamsById[teamId]?.team.userRole : undefined,
  );
  const trialEndsAt = useTeamStore((s) =>
    teamId ? s.teamsById[teamId]?.team.trialEndsAt : undefined,
  );
  const subscriptionStatus = useTeamStore((s) =>
    teamId ? s.teamsById[teamId]?.team.subscriptionStatus : undefined,
  );
  const cancelAtPeriodEnd = useTeamStore((s) =>
    teamId ? Boolean(s.teamsById[teamId]?.team.cancelAtPeriodEnd) : false,
  );

  const trialBannerState = useMemo(
    () =>
      getTrialBannerStage({
        subscriptionStatus,
        cancelAtPeriodEnd,
        trialEndsAt,
      }),
    [subscriptionStatus, cancelAtPeriodEnd, trialEndsAt],
  );

  const isDismissible = trialBannerState
    ? isTrialBannerDismissible(trialBannerState.stage)
    : false;

  const dismissKey =
    userId && trialBannerState
      ? buildTrialBannerDismissKey({
          userId,
          stage: trialBannerState.stage,
          daysLeft: trialBannerState.daysLeft,
        })
      : null;

  const [isDismissed, setIsDismissed] = useState(() =>
    dismissKey ? readTrialBannerDismissed(dismissKey) : false,
  );

  useEffect(() => {
    setIsDismissed(dismissKey ? readTrialBannerDismissed(dismissKey) : false);
  }, [dismissKey]);

  const dismiss = useCallback(() => {
    if (!dismissKey) {
      return;
    }
    writeTrialBannerDismissed(dismissKey);
    setIsDismissed(true);
  }, [dismissKey]);

  const goToBilling = useCallback(() => {
    navigate(APP_PAGES.BILLING);
  }, [navigate]);

  const isVisible = shouldShowTrialBanner({
    trialBannerState,
    pathname,
    isDismissed,
  });

  return {
    isVisible,
    stage: trialBannerState?.stage ?? null,
    daysLeft: trialBannerState?.daysLeft ?? 0,
    trialEndsAt,
    teamId,
    isDismissible,
    canManage: isTeamAdminOrOwner(userRole),
    dismiss,
    goToBilling,
  };
}
